import { Button, ButtonSize } from '@leland-dev/leland-ui-library';
import React, { type FC } from 'react';

import { Oops } from '../Oops';
import { PackageReviewInfo } from '../coach/PackageReviewInfo';
import { ReviewList } from '../coach/ReviewList';
import { Outcomes } from '../outcomes/Outcomes';

import {
  type PackageReviewsCategoryFragment,
  usePackageReviewsReviewsForPackageQuery,
} from './__generated-gql-types__/PackageReviews.generated';

const DEFAULT_REVIEWS_COUNT = 4;

interface PackageReviewsProps {
  category: PackageReviewsCategoryFragment;
  onViewAllReviews: () => void;
}

const PackageReviews: FC<PackageReviewsProps> = ({
  category,
  onViewAllReviews,
}) => {
  const {
    data: limitedReviews,
    loading: limitedReviewsLoading,
    error: limitedReviewsError,
    refetch: refetchLimitedReviews,
  } = usePackageReviewsReviewsForPackageQuery({
    variables: {
      categoryUrn: category.id,
      count: DEFAULT_REVIEWS_COUNT,
    },
  });

  return (
    <div className="flex flex-row">
      {limitedReviewsLoading ? (
        <div className="my-4 w-full text-center">Loading reviews...</div>
      ) : limitedReviewsError ? (
        <Oops
          description="We encountered an error fetching reviews. Please try again."
          retry={refetchLimitedReviews}
        />
      ) : limitedReviews && limitedReviews.reviewsForPackage.total > 0 ? (
        <>
          <div className="mt-0 flex w-full flex-col">
            <div className="mb-8 border-b py-8">
              <h2 className="text-3xl font-medium text-leland-gray-dark">
                Read reviews from customers who purchased {category.name}{' '}
                packages.
              </h2>
              <p className="text-leland-gray-light">
                Packages maximize your chances of reaching your goals
              </p>
            </div>
            <div className="flex flex-col gap-8 sm:flex-row sm:gap-16">
              <div className="flex flex-col sm:w-1/3">
                <PackageReviewInfo category={category} />
                {category.outcomes ? (
                  <Outcomes outcomes={category.outcomes} coachName={null} />
                ) : null}
              </div>
              <ReviewList
                reviews={
                  limitedReviews?.reviewsForPackage.results.slice(0, 4) ?? []
                }
                className="w-full"
                showCoachAttribution
              />
            </div>
            {limitedReviews.reviewsForPackage.total > DEFAULT_REVIEWS_COUNT ? (
              <div className="mt-6 flex w-full justify-center">
                <Button
                  label={`See all ${limitedReviews?.reviewsForPackage.total} reviews`}
                  onClick={onViewAllReviews}
                  size={ButtonSize.LARGE}
                  id="package_reviews--see_all_reviews"
                />
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default PackageReviews;
